import type { ReactNode } from "react";
import { Container, MapPin, Package, Tag, Tags, Boxes, ClipboardList, Truck, Route, Eraser, Inbox, Trophy, FileCheck, PackageMinus, Layers, ListChecks, FileSpreadsheet, Combine } from "lucide-react";
import AdminPageHeader from "../components/admin-page-header";
import AdminTile from "../components/admin/AdminTile";
import type { AdminTileTone } from "../components/admin/AdminTile";
import "../styles/admin-home.css";

// Warehouse group page. Orders is no longer listed here — it lives as
// a top-level tile on /admin since it's the most-used admin section.
// Uses page-data-wide (not .admin-home) so its breadcrumb lines up
// vertically with sibling sub-pages like /admin/warehouse/totes.
// Tiles are listed alphabetically by their English label — keep new
// tiles in that order when adding one.
//
// Twenty tiles at the roomy /admin size ran to two screens, so this
// grid uses AdminTile's compact variant (icon beside the label, one
// short description line) and lets the grid run to five columns when
// the page is wide enough — see .admin-home-tiles--compact.
const ICON = { size: 20, strokeWidth: 1.6 } as const;

interface WarehouseTileDef {
    to: string;
    labelKey: string;
    descriptionKey: string;
    icon: ReactNode;
    tone: AdminTileTone;
}

const TILES: WarehouseTileDef[] = [
    { to: "/admin/boxes",                     labelKey: "boxes_settings",           descriptionKey: "warehouse_tile_boxes_desc",           icon: <Package {...ICON} />,       tone: "amber" },
    { to: "/admin/warehouse/bundles",         labelKey: "bundles_settings",         descriptionKey: "warehouse_tile_bundles_desc",         icon: <Combine {...ICON} />,       tone: "teal" },
    { to: "/admin/warehouse/inbound",         labelKey: "inbound_admin_title",      descriptionKey: "warehouse_tile_inbound_desc",         icon: <Truck {...ICON} />,         tone: "orange" },
    { to: "/admin/warehouse/inventory",       labelKey: "inventory_title",          descriptionKey: "warehouse_tile_inventory_desc",       icon: <Boxes {...ICON} />,         tone: "green" },
    { to: "/admin/warehouse/lanes",           labelKey: "lanes_settings",           descriptionKey: "warehouse_tile_lanes_desc",           icon: <Route {...ICON} />,         tone: "teal" },
    { to: "/admin/warehouse/locations",       labelKey: "locations_settings",       descriptionKey: "warehouse_tile_locations_desc",       icon: <MapPin {...ICON} />,        tone: "orange" },
    { to: "/admin/warehouse/manifest",        labelKey: "manifest_settings",        descriptionKey: "warehouse_tile_manifest_desc",        icon: <ClipboardList {...ICON} />, tone: "teal" },
    { to: "/admin/warehouse/manual-products", labelKey: "manual_products_settings", descriptionKey: "warehouse_tile_manual_products_desc", icon: <Tags {...ICON} />,          tone: "amber" },
    { to: "/admin/warehouse/outbound",        labelKey: "outbound_title",           descriptionKey: "warehouse_tile_outbound_desc",        icon: <Truck {...ICON} />,         tone: "green" },
    { to: "/admin/warehouse/pallets",         labelKey: "pallets_admin_title",      descriptionKey: "warehouse_tile_pallets_desc",         icon: <Layers {...ICON} />,        tone: "amber" },
    { to: "/admin/picking",                   labelKey: "picking_admin_title",      descriptionKey: "warehouse_tile_picking_desc",         icon: <ListChecks {...ICON} />,    tone: "green" },
    { to: "/admin/warehouse/points",          labelKey: "points_settings_title",    descriptionKey: "warehouse_tile_points_desc",          icon: <Trophy {...ICON} />,        tone: "amber" },
    { to: "/admin/warehouse/pos",             labelKey: "pos_settings",             descriptionKey: "warehouse_tile_pos_desc",             icon: <FileCheck {...ICON} />,     tone: "blue" },
    { to: "/admin/products",                  labelKey: "products_settings",        descriptionKey: "warehouse_tile_products_desc",        icon: <Tag {...ICON} />,           tone: "purple" },
    { to: "/admin/warehouse/putaway-queue",   labelKey: "putaway_queue_title",      descriptionKey: "putaway_queue_tile_desc",             icon: <Inbox {...ICON} />,         tone: "amber" },
    { to: "/admin/warehouse/relabel",         labelKey: "relabel_settings",         descriptionKey: "warehouse_tile_relabel_desc",         icon: <Tags {...ICON} />,          tone: "purple" },
    { to: "/admin/warehouse/reports",         labelKey: "reports_title",            descriptionKey: "warehouse_tile_reports_desc",         icon: <FileSpreadsheet {...ICON} />, tone: "blue" },
    { to: "/admin/suppliers",                 labelKey: "suppliers_settings",       descriptionKey: "warehouse_tile_suppliers_desc",       icon: <Truck {...ICON} />,         tone: "slate" },
    { to: "/admin/warehouse/totes",           labelKey: "totes_settings",           descriptionKey: "warehouse_tile_totes_desc",           icon: <Container {...ICON} />,     tone: "blue" },
    { to: "/admin/warehouse/wipe",            labelKey: "wipe_settings",            descriptionKey: "warehouse_tile_wipe_desc",            icon: <Eraser {...ICON} />,        tone: "slate" },
    // Write Off keeps its long description for the hint on its own page;
    // the tile uses the short one.
    { to: "/admin/warehouse/write-off",       labelKey: "write_off",                descriptionKey: "warehouse_tile_write_off_short",      icon: <PackageMinus {...ICON} />,  tone: "orange" },
];

const Warehouse = () => (
    <div className="page-data page-data-wide">
        <AdminPageHeader
            titleKey="warehouse_menu_title"
            backTo="/admin"
            hideSearch
            crumbs={[
                { labelKey: "admin_panel", to: "/admin" },
                { labelKey: "warehouse_menu_title" },
            ]}
        />
        <div className="admin-home-tiles admin-home-tiles--compact">
            {TILES.map(tile => (
                <AdminTile key={tile.to} compact {...tile} />
            ))}
        </div>
    </div>
);

export default Warehouse;
